/**
 * Kundali Table Components
 * Tables and display components for Kundali data
 */

import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { LAYMAN_TIPS } from '../../data/kundaliConstants';
import {
  calculateManglikDosha,
  calculatePlanetaryStrength,
  getCurrentPlanetaryPeriod,
  calculateHouseAnalysis
} from '../../utils/kundaliCalculations';

/**
 * Tooltip component — shows a (?) icon, on hover/tap reveals an explanation
 */
export const Tip = ({ tipKey }) => {
  const { language } = useLanguage();
  const tips = LAYMAN_TIPS[language] || LAYMAN_TIPS.en;
  const text = tips[tipKey];
  if (!text) return null;
  return (
    <span className="layman-tip" tabIndex={0} role="button" aria-label="More info">
      <span className="tip-icon">ⓘ</span>
      <span className="tip-text">{text}</span>
    </span>
  );
};

/**
 * Planetary Strength Analysis Table Component
 */
export const PlanetaryStrengthTable = ({ planets, lagnaSignIndex = 0 }) => {
  const { t } = useLanguage();
  const manglik = calculateManglikDosha(planets, lagnaSignIndex);

  // Planet name translations
  const getPlanetName = (name) => {
    const planetKeys = {
      'Sun': 'planetSun', 'Moon': 'planetMoon', 'Mars': 'planetMars',
      'Mercury': 'planetMercury', 'Jupiter': 'planetJupiter', 'Venus': 'planetVenus',
      'Saturn': 'planetSaturn', 'Rahu': 'planetRahu', 'Ketu': 'planetKetu'
    };
    return t(planetKeys[name]) || name;
  };

  // Dignity status translations
  const getDignityStatus = (status) => {
    const dignityKeys = {
      'Exalted': 'exalted', 'Debilitated': 'debilitated', 'Own Sign': 'ownSign',
      'Combust': 'combust', 'Neutral': 'neutral'
    };
    return t(dignityKeys[status]) || status;
  };

  // Level translations
  const getLevel = (level) => {
    const levelKeys = { 'Strong': 'strong', 'Medium': 'medium', 'Weak': 'weak' };
    return t(levelKeys[level]) || level;
  };

  // Manglik level translations
  const getManglikLevel = (level) => {
    const levelKeys = {
      'Low': 'manglikLow', 'Medium': 'manglikMedium', 'High': 'manglikHigh',
      'Cancelled': 'manglikCancelled', 'None': 'manglikNo'
    };
    return t(levelKeys[level]) || level;
  };

  const planetsWithStrength = planets.map(planet => ({
    ...planet,
    ...calculatePlanetaryStrength(planet, planets)
  }));

  const getStrengthColor = (level) => {
    if (level === 'Strong') return '#4CAF50';
    if (level === 'Weak') return '#f44336';
    return '#FF9800';
  };

  const getStrengthBar = (strength) => (
    <div style={{
      width: '100%',
      height: '8px',
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      <div style={{
        width: `${strength}%`,
        height: '100%',
        background: strength >= 70 ? '#4CAF50' : strength <= 30 ? '#f44336' : '#FF9800',
        transition: 'width 0.3s'
      }} />
    </div>
  );

  return (
    <div className="planetary-strength-table">
      <h4 style={{ color: 'var(--accent-gold)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        🔮 {t('planetaryStrength')} <Tip tipKey="strength" />
      </h4>

      {/* Manglik Status Card */}
      <div className="manglik-status" style={{
        background: manglik.isManglik ? 'rgba(255, 107, 107, 0.1)' : 'rgba(76, 175, 80, 0.1)',
        border: `1px solid ${manglik.isManglik ? '#FF6B6B' : '#4CAF50'}`,
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{t('manglikDosha')} <Tip tipKey="manglik" />:</span>
          <span style={{
            marginLeft: '0.5rem',
            fontWeight: 'bold',
            color: manglik.isManglik ? '#FF6B6B' : '#4CAF50'
          }}>
            {manglik.isManglik
              ? `${t('manglikYes')} (${getManglikLevel(manglik.level)}) - ${t('manglikHouse')} ${manglik.house}`
              : t('manglikNo')}
          </span>
        </div>
        {manglik.level === 'Cancelled' && (
          <span style={{ background: '#4CAF50', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem' }}>
            {t('manglikCancelled')}
          </span>
        )}
      </div>

      <table className="kundali-table strength-table">
        <thead>
          <tr>
            <th>{t('planet')}</th>
            <th>{t('dignity')} <Tip tipKey="dignity" /></th>
            <th>{t('strength')}</th>
            <th>{t('level')}</th>
          </tr>
        </thead>
        <tbody>
          {planetsWithStrength.map((planet) => (
            <tr key={planet.name}>
              <td>
                <span style={{ color: planet.color, marginRight: '0.5rem' }}>{planet.symbol}</span>
                {getPlanetName(planet.name)}
              </td>
              <td>
                <span style={{
                  color: planet.dignityStatus === 'Exalted' ? '#4CAF50' :
                         planet.dignityStatus === 'Debilitated' ? '#f44336' :
                         planet.dignityStatus === 'Own Sign' ? '#2196F3' :
                         'var(--text-secondary)'
                }}>
                  {getDignityStatus(planet.dignityStatus)}
                </span>
              </td>
              <td style={{ width: '120px' }}>
                {getStrengthBar(planet.strength)}
              </td>
              <td>
                <span style={{
                  background: getStrengthColor(planet.level),
                  color: 'white',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  {getLevel(planet.level)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Weekly Planetary Period Component
 */
export const WeeklyPlanetaryPeriod = () => {
  const { t } = useLanguage();
  const periods = getCurrentPlanetaryPeriod();

  // Planet name translations
  const getPlanetName = (name) => {
    const planetKeys = {
      'Sun': 'planetSun', 'Moon': 'planetMoon', 'Mars': 'planetMars',
      'Mercury': 'planetMercury', 'Jupiter': 'planetJupiter', 'Venus': 'planetVenus',
      'Saturn': 'planetSaturn'
    };
    return t(planetKeys[name]) || name;
  };

  // Day name translations
  const getDayName = (day) => {
    const dayKeys = {
      'Sunday': 'daySunday', 'Monday': 'dayMonday', 'Tuesday': 'dayTuesday',
      'Wednesday': 'dayWednesday', 'Thursday': 'dayThursday', 'Friday': 'dayFriday',
      'Saturday': 'daySaturday'
    };
    return t(dayKeys[day]) || day;
  };

  const planetColors = {
    'Sun': '#FF6B00', 'Moon': '#C0C0C0', 'Mars': '#FF0000',
    'Mercury': '#32CD32', 'Jupiter': '#FFD700', 'Venus': '#FF69B4', 'Saturn': '#4169E1'
  };

  return (
    <div className="weekly-period-card">
      <h4 style={{ color: 'var(--accent-gold)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        📅 {t('weeklyPlanetaryPeriods')}
      </h4>

      {/* Current Period Highlight */}
      <div className="current-period-highlight" style={{
        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 140, 0, 0.1) 100%)',
        border: '2px solid var(--accent-gold)',
        borderRadius: '12px',
        padding: '1rem 1.5rem',
        marginBottom: '1.5rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
          {t('todaysRulingPlanet')}
        </div>
        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: planetColors[periods.currentDayLord] }}>
          {getPlanetName(periods.currentDayLord)}
        </div>
        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
          {t('currentHoraLord')}: <span style={{ color: planetColors[periods.horaLord], fontWeight: 'bold' }}>{getPlanetName(periods.horaLord)}</span>
        </div>
      </div>

      {/* Weekly Schedule */}
      <div className="weekly-schedule" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
        gap: '0.75rem'
      }}>
        {periods.weeklyPeriods.map((period) => (
          <div key={period.day} style={{
            background: period.isToday ? 'rgba(255, 215, 0, 0.2)' : 'rgba(255,255,255,0.05)',
            border: period.isToday ? '2px solid var(--accent-gold)' : '1px solid var(--border-color)',
            borderRadius: '10px',
            padding: '0.75rem',
            textAlign: 'center',
            transition: 'transform 0.2s',
            cursor: 'default'
          }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{getDayName(period.day)}</div>
            <div style={{
              fontSize: '1rem',
              fontWeight: 'bold',
              color: planetColors[period.lord],
              marginTop: '0.25rem'
            }}>
              {getPlanetName(period.lord)}
            </div>
            {period.isToday && (
              <div style={{
                fontSize: '0.65rem',
                background: 'var(--accent-gold)',
                color: 'black',
                borderRadius: '8px',
                padding: '0.1rem 0.4rem',
                marginTop: '0.25rem'
              }}>
                {t('today')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * House Analysis with Remedies Component
 */
export const HouseAnalysisTable = ({ planets, ascendantSign }) => {
  const { t } = useLanguage();
  const houses = calculateHouseAnalysis(planets, ascendantSign);
  const weakHouses = houses.filter(h => h.status === 'Weak');

  // Sign name translations
  const getSignName = (sign) => {
    const signKeys = {
      'Aries': 'signAries', 'Taurus': 'signTaurus', 'Gemini': 'signGemini',
      'Cancer': 'signCancer', 'Leo': 'signLeo', 'Virgo': 'signVirgo',
      'Libra': 'signLibra', 'Scorpio': 'signScorpio', 'Sagittarius': 'signSagittarius',
      'Capricorn': 'signCapricorn', 'Aquarius': 'signAquarius', 'Pisces': 'signPisces'
    };
    return t(signKeys[sign]) || sign;
  };

  // Planet name translations
  const getPlanetName = (name) => {
    const planetKeys = {
      'Sun': 'planetSun', 'Moon': 'planetMoon', 'Mars': 'planetMars',
      'Mercury': 'planetMercury', 'Jupiter': 'planetJupiter', 'Venus': 'planetVenus',
      'Saturn': 'planetSaturn', 'Rahu': 'planetRahu', 'Ketu': 'planetKetu'
    };
    return t(planetKeys[name]) || name;
  };

  // House significance translations
  const getHouseSignificance = (houseNum) => {
    return t(`house${houseNum}Sig`) || houses.find(h => h.number === houseNum)?.significance;
  };

  // Status translations
  const getStatus = (status) => {
    const statusKeys = { 'Strong': 'houseStrong', 'Moderate': 'houseModerate', 'Weak': 'houseWeak' };
    return t(statusKeys[status]) || status;
  };

  // Day translations for remedies
  const getDayName = (day) => {
    const dayKeys = {
      'Sunday': 'daySunday', 'Monday': 'dayMonday', 'Tuesday': 'dayTuesday',
      'Wednesday': 'dayWednesday', 'Thursday': 'dayThursday', 'Friday': 'dayFriday',
      'Saturday': 'daySaturday'
    };
    return t(dayKeys[day]) || day;
  };

  const getStatusColor = (status) => {
    if (status === 'Strong') return '#4CAF50';
    if (status === 'Weak') return '#f44336';
    return '#FF9800';
  };

  return (
    <div className="house-analysis">
      <h4 style={{ color: 'var(--accent-gold)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        🏠 {t('houseAnalysis')} <Tip tipKey="houseAnalysis" />
      </h4>

      <table className="kundali-table house-table">
        <thead>
          <tr>
            <th>{t('house')}</th>
            <th>{t('sign')}</th>
            <th>{t('rashiLord')}</th>
            <th>{t('significance')}</th>
            <th>{t('planets')}</th>
            <th>{t('status')}</th>
          </tr>
        </thead>
        <tbody>
          {houses.map((house) => (
            <tr key={house.number} style={{
              background: house.status === 'Weak' ? 'rgba(244, 67, 54, 0.1)' : 'transparent'
            }}>
              <td style={{ fontWeight: 'bold', color: 'var(--accent-gold)' }}>{house.number}</td>
              <td>{getSignName(house.sign)}</td>
              <td>{getPlanetName(house.lord)}</td>
              <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{getHouseSignificance(house.number)}</td>
              <td>{house.planets.map(p => getPlanetName(p)).join(', ') || '-'}</td>
              <td>
                <span style={{
                  background: getStatusColor(house.status),
                  color: 'white',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem'
                }}>
                  {getStatus(house.status)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Remedies Section for Weak Houses */}
      {weakHouses.length > 0 && (
        <div className="remedies-section" style={{ marginTop: '2rem' }}>
          <h5 style={{ color: 'var(--accent-saffron)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            ⚕️ {t('recommendedRemedies')} <Tip tipKey="remedy" />
          </h5>
          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {weakHouses.map((house) => house.remedy && (
              <div key={house.number} className="remedy-card" style={{
                background: 'rgba(255, 107, 107, 0.08)',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '12px',
                padding: '1rem'
              }}>
                <div style={{ fontWeight: 'bold', color: 'var(--accent-gold)', marginBottom: '0.5rem' }}>
                  {t('house')} {house.number} - {getPlanetName(house.lord)}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                  {getHouseSignificance(house.number)}
                </div>
                <div style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                  <div>💎 <strong>{t('gemstone')}:</strong> {house.remedy.gemstone}</div>
                  <div>🔔 <strong>{t('mantra')}:</strong> <em>{house.remedy.mantra}</em></div>
                  <div>🎨 <strong>{t('wearColor')}:</strong> {house.remedy.color} {t('on')} {getDayName(house.remedy.day)}</div>
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    background: 'rgba(255, 215, 0, 0.1)',
                    borderRadius: '8px',
                    fontSize: '0.85rem'
                  }}>
                    ⏱️ <strong>{t('duration')}:</strong> {t('followFor')} {house.remedy.duration}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Planetary Position Table
 */
export const PlanetaryTable = ({ planets }) => {
  const { t } = useLanguage();

  // Planet name translations
  const getPlanetName = (name) => {
    const planetKeys = {
      'Sun': 'planetSun', 'Moon': 'planetMoon', 'Mars': 'planetMars',
      'Mercury': 'planetMercury', 'Jupiter': 'planetJupiter', 'Venus': 'planetVenus',
      'Saturn': 'planetSaturn', 'Rahu': 'planetRahu', 'Ketu': 'planetKetu'
    };
    return t(planetKeys[name]) || name;
  };

  // Sign name translations
  const getSignName = (sign) => {
    const signKeys = {
      'Aries': 'signAries', 'Taurus': 'signTaurus', 'Gemini': 'signGemini',
      'Cancer': 'signCancer', 'Leo': 'signLeo', 'Virgo': 'signVirgo',
      'Libra': 'signLibra', 'Scorpio': 'signScorpio', 'Sagittarius': 'signSagittarius',
      'Capricorn': 'signCapricorn', 'Aquarius': 'signAquarius', 'Pisces': 'signPisces'
    };
    return t(signKeys[sign]) || sign;
  };

  return (
    <div className="planetary-table">
      <h4 style={{ color: 'var(--accent-gold)', marginBottom: '1rem' }}>
        {t('planetaryPositions')} <Tip tipKey="lagnaChart" />
      </h4>
      <table className="kundali-table">
        <thead>
          <tr>
            <th>{t('planet')}</th>
            <th>{t('sign')}</th>
            <th>{t('degree')} <Tip tipKey="degree" /></th>
            <th>{t('house')} <Tip tipKey="house" /></th>
            <th>{t('nakshatra')} <Tip tipKey="nakshatra" /></th>
            <th>{t('status')}</th>
          </tr>
        </thead>
        <tbody>
          {planets.map((planet) => (
            <tr key={planet.name}>
              <td>
                <span style={{ color: planet.color }}>{planet.symbol}</span> {getPlanetName(planet.name)}
              </td>
              <td>{getSignName(planet.sign)}</td>
              <td>{planet.degree}°{planet.minutes}'{planet.seconds}"</td>
              <td>{planet.house}</td>
              <td>{planet.nakshatra} ({planet.pada})</td>
              <td>
                {planet.retrograde && <span className="retrograde-badge" title="Retrograde">℞</span>}
                {planet.combust && <span className="combust-badge" title="Combust">☼</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Dosha Summary Card Component
 * Shows all doshas in a layman-friendly format
 */
export const DoshaSummaryCard = ({ doshaSummary }) => {
  const { language, t } = useLanguage();
  const [expandedDosha, setExpandedDosha] = React.useState(null);

  if (!doshaSummary) return null;

  const { doshas, summary, noDoshas } = doshaSummary;

  // Filter actual doshas (not info items)
  const actualDoshas = doshas.filter(d => d.present && !d.isInfo);
  const infoItems = doshas.filter(d => d.isInfo);

  const getSeverityLabel = (severity) => {
    const labels = {
      'High': language === 'te' ? 'తీవ్రం' : 'High',
      'Medium': language === 'te' ? 'మధ్యస్థం' : 'Medium',
      'Low': language === 'te' ? 'తక్కువ' : 'Low',
      'Cancelled': language === 'te' ? 'రద్దు ✓' : 'Cancelled ✓',
    };
    return labels[severity] || severity;
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return '#f44336';
      case 'Medium': return '#FF9800';
      case 'Low': return '#8BC34A';
      case 'Cancelled': return '#4CAF50';
      default: return '#9E9E9E';
    }
  };

  return (
    <div className="dosha-summary-card">
      {/* Header with Overall Status */}
      <div className="dosha-header" style={{
        background: `linear-gradient(135deg, ${summary.overallColor}20 0%, ${summary.overallColor}10 100%)`,
        border: `2px solid ${summary.overallColor}`,
        borderRadius: '16px',
        padding: '1.25rem',
        marginBottom: '1.5rem',
        textAlign: 'center'
      }}>
        <h4 style={{
          color: 'var(--accent-gold)',
          marginBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          fontSize: '1.1rem'
        }}>
          🪬 {language === 'te' ? 'మీ జాతకంలో ఉన్న దోషాలు' : 'Doshas in Your Horoscope'}
        </h4>

        <div style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          color: summary.overallColor,
          marginBottom: '0.5rem'
        }}>
          {language === 'te' ? summary.overallStatusTe : summary.overallStatus}
        </div>

        {noDoshas ? (
          <p style={{ color: '#4CAF50', fontSize: '0.95rem', margin: 0 }}>
            ✨ {language === 'te'
              ? 'మీ జాతకంలో పెద్ద దోషాలు కనిపించలేదు! శుభం!'
              : 'No major doshas found in your horoscope! Auspicious!'}
          </p>
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1.5rem',
            marginTop: '0.75rem',
            flexWrap: 'wrap'
          }}>
            {summary.high > 0 && (
              <span style={{
                background: 'rgba(244, 67, 54, 0.2)',
                color: '#f44336',
                padding: '0.3rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}>
                {summary.high} {language === 'te' ? 'తీవ్రం' : 'High'}
              </span>
            )}
            {summary.medium > 0 && (
              <span style={{
                background: 'rgba(255, 152, 0, 0.2)',
                color: '#FF9800',
                padding: '0.3rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}>
                {summary.medium} {language === 'te' ? 'మధ్యస్థం' : 'Medium'}
              </span>
            )}
            {summary.low > 0 && (
              <span style={{
                background: 'rgba(139, 195, 74, 0.2)',
                color: '#8BC34A',
                padding: '0.3rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}>
                {summary.low} {language === 'te' ? 'తక్కువ/రద్దు' : 'Low/Cancelled'}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Dosha Cards */}
      {actualDoshas.length > 0 && (
        <div className="dosha-list" style={{
          display: 'grid',
          gap: '1rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'
        }}>
          {actualDoshas.map((dosha, index) => (
            <div
              key={index}
              className="dosha-item"
              style={{
                background: `linear-gradient(135deg, ${dosha.color}15 0%, ${dosha.color}08 100%)`,
                border: `1px solid ${dosha.color}40`,
                borderRadius: '12px',
                padding: '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onClick={() => setExpandedDosha(expandedDosha === index ? null : index)}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{dosha.icon}</span>
                  <div>
                    <div style={{
                      fontWeight: 'bold',
                      color: dosha.color,
                      fontSize: '1rem'
                    }}>
                      {language === 'te' ? dosha.nameTe : dosha.name}
                    </div>
                  </div>
                </div>
                <span style={{
                  background: getSeverityColor(dosha.severity),
                  color: 'white',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {getSeverityLabel(dosha.severity)}
                </span>
              </div>

              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '0.85rem',
                margin: '0.5rem 0',
                lineHeight: '1.5'
              }}>
                {language === 'te' ? dosha.descriptionTe : dosha.description}
              </p>

              {/* Expandable Remedy Section */}
              {expandedDosha === index && (
                <div style={{
                  marginTop: '0.75rem',
                  paddingTop: '0.75rem',
                  borderTop: `1px dashed ${dosha.color}40`,
                  animation: 'fadeIn 0.3s ease'
                }}>
                  <div style={{
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: 'var(--accent-gold)',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem'
                  }}>
                    ⚕️ {language === 'te' ? 'పరిహారం:' : 'Remedy:'}
                  </div>
                  <p style={{
                    color: 'var(--text-primary)',
                    fontSize: '0.85rem',
                    margin: 0,
                    lineHeight: '1.6',
                    background: 'rgba(255, 215, 0, 0.1)',
                    padding: '0.75rem',
                    borderRadius: '8px'
                  }}>
                    {language === 'te' ? dosha.remedyTe : dosha.remedy}
                  </p>
                </div>
              )}

              <div style={{
                textAlign: 'center',
                marginTop: '0.5rem',
                color: 'var(--text-secondary)',
                fontSize: '0.75rem'
              }}>
                {expandedDosha === index
                  ? (language === 'te' ? '▲ మూసివేయి' : '▲ Close')
                  : (language === 'te' ? '▼ పరిహారం చూడండి' : '▼ See Remedy')}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Items (like Nadi Type) */}
      {infoItems.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h5 style={{
            color: 'var(--text-secondary)',
            fontSize: '0.9rem',
            marginBottom: '0.75rem'
          }}>
            ℹ️ {language === 'te' ? 'అదనపు సమాచారం' : 'Additional Information'}
          </h5>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}>
            {infoItems.map((item, index) => (
              <div key={index} style={{
                background: `${item.color}15`,
                border: `1px solid ${item.color}40`,
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                flex: '1 1 auto',
                minWidth: '200px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.25rem'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                  <span style={{ fontWeight: '600', color: item.color }}>
                    {language === 'te' ? item.nameTe : item.name}
                  </span>
                </div>
                <div style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'var(--text-primary)'
                }}>
                  {language === 'te' ? item.severityTe : item.severity}
                </div>
                <p style={{
                  fontSize: '0.8rem',
                  color: 'var(--text-secondary)',
                  margin: '0.25rem 0 0'
                }}>
                  {language === 'te' ? item.descriptionTe : item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <p style={{
        textAlign: 'center',
        fontSize: '0.75rem',
        color: 'var(--text-secondary)',
        marginTop: '1.5rem',
        fontStyle: 'italic',
        padding: '0.75rem',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '8px'
      }}>
        {language === 'te'
          ? '⚠️ దోషాలు కేవలం సూచనలు మాత్రమే. వ్యక్తిగత సలహా కోసం అనుభవజ్ఞుడైన జ్యోతిష్కుని సంప్రదించండి. అనేక దోషాలకు రద్దు నియమాలు ఉంటాయి.'
          : '⚠️ Doshas are indications only. Many have cancellation rules. Consult an experienced astrologer for personalized advice.'}
      </p>
    </div>
  );
};

export default {
  Tip,
  PlanetaryStrengthTable,
  WeeklyPlanetaryPeriod,
  HouseAnalysisTable,
  PlanetaryTable,
  DoshaSummaryCard
};
